import { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle, Loader2, XCircle, ArrowRight, Calendar, Utensils, FileText } from "lucide-react";
import axios from "axios";
import Navigation from "@/components/Navigation";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("loading");
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [pollCount, setPollCount] = useState(0);

  const pollPaymentStatus = useCallback(async (attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus("timeout");
      return;
    }

    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`);
      const data = response.data;

      if (data.payment_status === "paid") {
        setStatus("success");
        setPaymentInfo(data);
        return;
      } else if (data.status === "expired") {
        setStatus("expired");
        return;
      }

      // Continue polling
      setPollCount(attempts + 1);
      setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
    } catch (error) {
      console.error("Error checking payment status:", error);
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
      } else {
        setStatus("error");
      }
    }
  }, [sessionId]);

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(0);
    } else {
      setStatus("no_session");
    }
  }, [sessionId, pollPaymentStatus]);

  const renderContent = () => {
    switch (status) {
      case "loading":
        return (
          <div className="text-center py-12">
            <Loader2 className="h-16 w-16 text-[#4FB3D4] animate-spin mx-auto mb-6" />
            <h2 className="font-heading text-2xl font-semibold text-[#1A2A3A] mb-2">
              Verifying Your Payment
            </h2>
            <p className="text-[#1A2A3A]/60">
              Please wait while we confirm your transaction...
              {pollCount > 0 && ` (Attempt ${pollCount + 1}/5)`}
            </p>
          </div>
        );

      case "success":
        return (
          <div className="text-center py-8">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-12 w-12 text-green-500" />
            </div>
            <h2 className="font-heading text-3xl font-bold text-[#1A2A3A] mb-4">
              Welcome to Your Recovery Journey!
            </h2>
            <p className="text-lg text-[#1A2A3A]/70 mb-8 max-w-lg mx-auto">
              Your payment was successful. You now have full access to all ThryvLife recovery resources.
            </p>

            {paymentInfo && (
              <div className="bg-[#4FB3D4]/5 rounded-2xl p-6 mb-8 max-w-md mx-auto">
                <p className="text-sm text-[#1A2A3A]/60 mb-2">Payment Confirmed</p>
                <p className="font-heading text-2xl font-bold text-[#4FB3D4]">
                  ${(paymentInfo.amount_total / 100).toFixed(2)} {paymentInfo.currency?.toUpperCase()}
                </p>
              </div>
            )}

            {/* Quick Access Cards */}
            <div className="grid md:grid-cols-3 gap-4 mb-8">
              <Link to="/calendar" className="block">
                <Card className="bg-white rounded-xl p-4 hover:shadow-lg transition-shadow card-hover">
                  <CardContent className="p-0 text-center">
                    <Calendar className="h-8 w-8 text-[#4FB3D4] mx-auto mb-2" />
                    <p className="font-medium text-[#1A2A3A]">Affirmation Calendar</p>
                  </CardContent>
                </Card>
              </Link>
              <Link to="/recipes" className="block">
                <Card className="bg-white rounded-xl p-4 hover:shadow-lg transition-shadow card-hover">
                  <CardContent className="p-0 text-center">
                    <Utensils className="h-8 w-8 text-[#FFB7B2] mx-auto mb-2" />
                    <p className="font-medium text-[#1A2A3A]">Health Recipes</p>
                  </CardContent>
                </Card>
              </Link>
              <Link to="/bonus" className="block">
                <Card className="bg-white rounded-xl p-4 hover:shadow-lg transition-shadow card-hover">
                  <CardContent className="p-0 text-center">
                    <FileText className="h-8 w-8 text-[#A69BE0] mx-auto mb-2" />
                    <p className="font-medium text-[#1A2A3A]">Bonus PDF</p>
                  </CardContent>
                </Card>
              </Link>
            </div>

            <Link to="/dashboard">
              <Button 
                className="btn-primary text-lg px-8 py-6 rounded-full font-semibold"
                data-testid="go-to-dashboard-btn"
              >
                Go to Dashboard
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        );

      case "expired":
        return (
          <div className="text-center py-12">
            <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="h-12 w-12 text-yellow-500" />
            </div>
            <h2 className="font-heading text-2xl font-semibold text-[#1A2A3A] mb-4">
              Session Expired
            </h2>
            <p className="text-[#1A2A3A]/60 mb-8">
              Your payment session has expired. Please try again.
            </p>
            <Link to="/checkout">
              <Button className="btn-primary">Try Again</Button>
            </Link>
          </div>
        );

      case "error":
      case "timeout":
        return (
          <div className="text-center py-12">
            <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="h-12 w-12 text-orange-500" />
            </div>
            <h2 className="font-heading text-2xl font-semibold text-[#1A2A3A] mb-4">
              Verification Issue
            </h2>
            <p className="text-[#1A2A3A]/60 mb-8">
              We couldn't verify your payment status. If you were charged, please contact support.
            </p>
            <div className="flex gap-4 justify-center">
              <Link to="/checkout">
                <Button variant="outline" className="rounded-full">Try Again</Button>
              </Link>
              <Link to="/">
                <Button className="btn-primary rounded-full">Go Home</Button>
              </Link>
            </div>
          </div>
        );

      case "no_session":
        return (
          <div className="text-center py-12">
            <h2 className="font-heading text-2xl font-semibold text-[#1A2A3A] mb-4">
              No Payment Session Found
            </h2>
            <p className="text-[#1A2A3A]/60 mb-8">
              It looks like you arrived here without completing a payment.
            </p>
            <Link to="/checkout">
              <Button className="btn-primary">Go to Checkout</Button>
            </Link>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Navigation />
      
      <section className="pt-32 pb-24 px-6 lg:px-12" data-testid="payment-success-section">
        <div className="container mx-auto max-w-3xl">
          <Card className="glass rounded-3xl shadow-floating border-0">
            <CardContent className="p-8 md:p-12">
              {renderContent()}
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default PaymentSuccessPage;
